import { Appointment, APPOINTMENT_CHANNEL, TYPES_OF_APT } from "@elixir/types";
import { capitalizeFirstLetter } from "@elixir/utils";
import { ReactNode } from "react";
import { TooltipComponent } from "ui";
import dateFormat from "dateformat";

const VISIT_TYPE_MAP: {
  [key: string]: {
    icon: ReactNode;
    titleTextClassName: string;
    followUpIconFillClassName: string;
    title: string;
  };
} = {
  TELE: {
    icon: (
      <svg
        className="w-16 h-16 dr-text-darwin-touch-neutral-400"
        viewBox="0 0 576 512"
      >
        <path
          d="M557.6 102.3c-11.53-7.406-25.88-8.391-38.28-2.688L416 147V128c0-35.35-28.65-64-64-64H64C28.65 64 0 92.65 0 128v256c0 35.35 28.65 64 64 64h288c35.35 0 64-28.65 64-64v-19.02l103.3 47.36c5.344 2.453 11.03 3.672 16.69 3.672c7.531 0 15.02-2.141 21.59-6.359C569.1 402.3 576 389.7 576 375.1V136C576 122.3 569.1 109.8 557.6 102.3zM368 384c0 8.822-7.178 16-16 16H64c-8.822 0-16-7.178-16-16V128c0-8.822 7.178-16 16-16h288c8.822 0 16 7.178 16 16V384zM528 363.5L416 312.2V199.8l112-51.33V363.5z"
          fill="currentColor"
        ></path>
      </svg>
    ),
    titleTextClassName: "dr-text-darwin-touch-neutral-1000",
    followUpIconFillClassName: "dr-fill-darwin-touch-neutral-400",
    title: "Teleconsultation",
  },
  WALKIN: {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
        className="dr-text-darwin-touch-neutral-400 w-16 h-16"
      >
        <path
          d="M208 96c26.5 0 48-21.5 48-48S234.5 0 208 0s-48 21.5-48 48 21.5 48 48 48zm94.5 149.1l-23.3-11.8-9.7-29.4c-14.7-44.6-55.7-75.8-102.2-75.9-36-.1-55.9 10.1-93.3 25.2-21.6 8.7-39.3 25.2-49.7 46.2L17.6 213c-7.8 15.8-1.5 35 14.2 42.9 15.6 7.9 34.6 1.5 42.5-14.3L81 228c3.5-7 9.3-12.5 16.5-15.4l26.8-10.8-15.2 60.7c-5.2 20.8.4 42.9 14.9 58.8l59.9 65.4c7.2 7.9 12.3 17.4 14.9 27.7l18.3 73.3c4.3 17.1 21.7 27.6 38.8 23.3 17.1-4.3 27.6-21.7 23.3-38.8l-22.2-89c-2.6-10.3-7.7-19.9-14.9-27.7l-45.5-49.7 17.2-68.7 5.5 16.5c5.3 16.1 16.7 29.4 31.7 37l23.3 11.8c15.6 7.9 34.6 1.5 42.5-14.3 7.7-15.7 1.4-35.1-14.3-43zM73.6 385.8c-3.2 8.1-8 15.4-14.2 21.5l-50 50.1c-12.5 12.5-12.5 32.8 0 45.3s32.7 12.5 45.2 0l59.4-59.4c6.1-6.1 10.9-13.4 14.2-21.5l13.5-33.8c-55.3-60.3-38.7-41.8-47.4-53.7l-20.7 51.5z"
          fill="currentColor"
        ></path>
      </svg>
    ),
    titleTextClassName: "dr-text-darwin-touch-neutral-1000",
    followUpIconFillClassName: "dr-text-darwin-touch-neutral-400",
    title: "Walkin",
  },
  default: {
    icon: (
      <svg
        className="w-14 h-14 dr-min-w-14 dr-text-darwin-touch-neutral-400"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
      >
        <path
          d="M216.1 408.1C207.6 418.3 192.4 418.3 183 408.1L119 344.1C109.7 335.6 109.7 320.4 119 311C128.4 301.7 143.6 301.7 152.1 311L200 358.1L295 263C304.4 253.7 319.6 253.7 328.1 263C338.3 272.4 338.3 287.6 328.1 296.1L216.1 408.1zM128 0C141.3 0 152 10.75 152 24V64H296V24C296 10.75 306.7 0 320 0C333.3 0 344 10.75 344 24V64H384C419.3 64 448 92.65 448 128V448C448 483.3 419.3 512 384 512H64C28.65 512 0 483.3 0 448V128C0 92.65 28.65 64 64 64H104V24C104 10.75 114.7 0 128 0zM400 192H48V448C48 456.8 55.16 464 64 464H384C392.8 464 400 456.8 400 448V192z"
          fill="currentColor"
        ></path>
      </svg>
    ),
    titleTextClassName: "dr-text-darwin-touch-neutral-1000",
    followUpIconFillClassName: "dr-fill-darwin-touch-neutral-400",
    title: "Appointment",
  },
};
const VisitType = ({
  apt,
  className = "dr-col-span-2",
}: {
  apt: Appointment;
  className?: string;
}) => {
  const { "visit-type": visitType, service, channel, start_time, mode } = apt;

  const config =
    visitType in VISIT_TYPE_MAP
      ? VISIT_TYPE_MAP[visitType]
      : VISIT_TYPE_MAP["default"];

  const title = capitalizeFirstLetter(service?.service_name || config.title);
  const formattedStartTime = start_time
    ? dateFormat(start_time, "hh:MM TT")
    : undefined;
  const toShowStartTime =
    mode && channel !== APPOINTMENT_CHANNEL.WALKIN && formattedStartTime;
  const titleText = `${
    toShowStartTime ? `Booked at: ${formattedStartTime}` : title
  }${visitType !== "WALKIN" && channel ? ` | ${channel}` : ``}`;

  return (
    <TooltipComponent title={titleText} placement="top">
      <div
        className={`BodyBody2Regular dr-text-darwin-touch-neutral-1000 dr-py-7 dr-px-8 dr-border-1 dr-border-darwin-touch-neutral-100 dr-rounded-4 flex items-center dr-h-32 ${className}`}
      >
        <div className="flex items-center space-x-8 dr-truncate">
          {config.icon}
          <div className="flex items-center gap-x-8 capitalize">
            <span className={`${config.titleTextClassName}`}>
              {toShowStartTime
                ? formattedStartTime
                : service?.service_name || config.title}
            </span>
            {service?.appointment_type === TYPES_OF_APT.FOLLOW_UP && (
              <>
                {" | "}
                <div className="w-16 h-16">
                  {followUpIcon(config.followUpIconFillClassName)}
                </div>
              </>
            )}
            {visitType !== "WALKIN" && (
              <>
                {channel ? " | " : ""}
                <p className="dr-text-darwin-touch-neutral-500">{channel}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </TooltipComponent>
  );
};

export default VisitType;

export const followUpIcon = (className?: string): JSX.Element => {
  return (
    <svg
      viewBox="0 0 12 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-12 h-12 ${className}`}
    >
      <path
        d="M11.25 7C10.8281 7 10.5 7.35156 10.5 7.75C10.5 8.99219 9.46875 10 8.25 10H4.5V9.0625C4.5 8.85156 4.35938 8.64062 4.14844 8.57031C3.96094 8.47656 3.72656 8.5 3.53906 8.66406L1.66406 10.3516C1.54688 10.4453 1.5 10.6094 1.5 10.75C1.5 10.9141 1.54688 11.0781 1.66406 11.1719L3.53906 12.8594C3.65625 12.9531 3.79688 13 3.9375 13C4.00781 13 4.07812 13 4.14844 12.9531C4.35938 12.8828 4.5 12.6719 4.5 12.4375V11.5H8.25C10.3125 11.5 12 9.83594 12 7.75C12 7.35156 11.6484 7 11.25 7ZM3.75 4H7.47656L7.5 4.9375C7.5 5.17188 7.61719 5.38281 7.82812 5.45312C7.89844 5.5 7.96875 5.5 8.03906 5.5C8.15625 5.5 8.29688 5.45312 8.41406 5.35938L10.2891 3.67188C10.4297 3.57812 10.5 3.41406 10.5 3.25C10.5 3.0625 10.4297 2.92188 10.3125 2.82812L8.4375 1.14062C8.27344 0.976562 8.01562 0.953125 7.82812 1.04688C7.61719 1.14062 7.47656 1.35156 7.47656 1.5625L7.5 2.5H3.75C1.66406 2.5 0 4.1875 0 6.25C0 6.67188 0.328125 7 0.75 7C1.14844 7 1.5 6.67188 1.5 6.25C1.5 5.03125 2.50781 4 3.75 4Z"
        fill="currentColor"
      />
    </svg>
  );
};
